import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { z } from "zod";
import LoginPageContent from "./-components/LoginPageContent";
import { buildBrandTitle } from "@nitro-ape/shared/lib/brand-config";
import { getEnv } from "@/lib/env";
import { useIdentityToken, usePrivy, useWallets } from "@privy-io/react-auth";
import { usePrivyAuth } from "@/hooks/use-privy-auth";

const schema = z.object({
  ref: z.string().optional(),
});

const EMBEDDED_WALLET_WARN_MS = 8_000;

export const Route = createFileRoute("/login/")({
  validateSearch: (search) => {
    return schema.parse(search);
  },
  head: () => ({
    meta: [{ title: buildBrandTitle(getEnv, "Login") }],
  }),
  component: LoginPage,
});

function LoginPage() {
  const { login, authenticated } = usePrivy();
  const { handlePrivyLogin, isLoading } = usePrivyAuth();
  const { wallets, ready } = useWallets();
  const { identityToken } = useIdentityToken();
  const exchangeStartedRef = useRef(false);

  const { ref } = useSearch({ from: "/login/" });

  // Trigger the backend token exchange as soon as Privy reports the
  // session is authenticated and an identity token is available. We
  // intentionally do NOT wait on an embedded (`walletClientType === 'privy'`)
  // wallet here: external-wallet-only logins (e.g. Metamask -> "Login with
  // Wallet") can take a few seconds for Privy to provision an embedded
  // wallet via `createOnLogin: "users-without-wallets"`, and the backend
  // doesn't need wallet metadata to mint app tokens. Downstream screens
  // (Monaco / HL onboarding) already guard on embedded-wallet readiness
  // separately.
  useEffect(() => {
    if (!authenticated || !ready || !identityToken) return;
    if (exchangeStartedRef.current) return;
    exchangeStartedRef.current = true;
    handlePrivyLogin(ref);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, ready, identityToken]);

  // Surface slow / missing embedded-wallet creation in the console so the
  // failure mode is visible during smoke testing instead of silently
  // hanging. Purely diagnostic — does not block the auth exchange.
  useEffect(() => {
    if (!authenticated || !ready) return;
    const hasEmbedded = wallets.some((w) => w.walletClientType === "privy");
    if (hasEmbedded) return;
    const timer = window.setTimeout(() => {
      console.warn(
        "[login] No embedded Privy wallet appeared within",
        EMBEDDED_WALLET_WARN_MS,
        "ms after authentication — check Privy `createOnLogin` config",
      );
    }, EMBEDDED_WALLET_WARN_MS);
    return () => window.clearTimeout(timer);
  }, [authenticated, ready, wallets]);

  return <LoginPageContent isLoading={isLoading} onLogin={login} />;
}
